// TDI-Brooks' standing operating regions, as jump targets.
//
// The point of this list is that nobody has to CREATE anything. A survey company
// works a known handful of places — the standing triad of the Gulf, the Caribbean
// and West Africa, plus the East Coast wind leases and the occasional Black Sea
// job — so the console should already know them. Naming, saving and re-loading an
// operating area is administrative work that earns nothing when the list barely
// changes from year to year.
//
// These are DEPARTURE POINTS, not fences. Picking one moves the map and the site
// there; the map stays free afterwards, and the field paints wherever you end up.
// Nothing here constrains what you can look at.
//
// Coordinates are the working centres named in the company brief, not administrative
// centroids: Walker Ridge rather than "the Gulf of Mexico", Stabroek rather than
// "Guyana". `spanNm` is only the width the view opens at.
//
// WHY THE PANEL IS CALLED WAYPOINTS AND THIS FILE STILL DESCRIBES REGIONS. The two
// kinds sit in one list and behave the same way — you click, the map goes there —
// but they are not the same thing, and `spanNm` is where the difference lives.
// A built-in below is a REGION: the Gulf of Mexico is 600 nm across and opening it
// should frame the basin. A waypoint someone saves is a POSITION, exact to where
// they clicked, and it carries no span at all (see savedwaypoints.ts). A position
// opens at whatever width the view is already set to, because the operator chose
// that width and a bookmark has no business overriding it.

export interface Waypoint {
  id: string;
  name: string;
  /** What happens there, in the operator's words — this is the disambiguator when
   *  two are geographically close. */
  note: string;
  lat: number;
  lon: number;
  /** View width to open at, nm. **Undefined means "a position, not a region"** —
   *  keep the current view width and simply go there. Only the built-in regions
   *  below set it. */
  spanNm?: number;
}

/** A waypoint being framed on the map, which always has a definite width — the
 *  one it declared, or the one the view was already set to. */
export type FramedWaypoint = Waypoint & { spanNm: number };

export const WAYPOINTS: readonly Waypoint[] = [
  {
    id: "gom",
    name: "Gulf of Mexico",
    note: "Walker Ridge, Green Canyon · Fourchon / Cameron",
    lat: 27.0,
    lon: -91.5,
    spanNm: 600,
  },
  {
    id: "gom-shelf",
    name: "GoM shelf & nearshore",
    note: "Barnacle, daylight-only nearshore work",
    lat: 28.9,
    lon: -92.5,
    spanNm: 250,
  },
  {
    id: "trinidad",
    name: "Trinidad",
    note: "Columbus Basin",
    lat: 10.5,
    lon: -61.0,
    spanNm: 250,
  },
  {
    id: "guyana-suriname",
    name: "Guyana – Suriname",
    note: "Stabroek · metocean buoy rotation",
    lat: 7.5,
    lon: -55.5,
    spanNm: 600,
  },
  {
    id: "brazil",
    name: "Brazil",
    note: "Campos & Santos basins",
    lat: -23.0,
    lon: -41.5,
    spanNm: 600,
  },
  {
    id: "guinea",
    name: "Gulf of Guinea",
    note: "Liberia, Ghana, Nigeria",
    lat: 3.5,
    lon: 2.0,
    spanNm: 1200,
  },
  {
    id: "angola",
    name: "Angola",
    note: "Lower Congo Basin",
    lat: -8.0,
    lon: 12.0,
    spanNm: 600,
  },
  {
    id: "useast",
    name: "US East Coast wind",
    note: "Atlantic lease areas",
    lat: 39.0,
    lon: -73.5,
    spanNm: 600,
  },
  {
    id: "blacksea",
    name: "Black Sea",
    note: "Türkiye — occasional",
    lat: 43.0,
    lon: 33.0,
    spanNm: 600,
  },
  {
    id: "timor-leste",
    name: "Timor-Leste",
    note: "MPRM Lot 1 Geotechnical Survey · Kelp Deep",
    lat: -10.25,
    lon: 127.0,
    spanNm: 500,
  },
];

export function waypointById(id: string): Waypoint | null {
  return WAYPOINTS.find((a) => a.id === id) ?? null;
}

/** The built-in region whose centre is nearest a point, within `withinNm`. Used to
 *  say "you are in the Gulf" without asking anyone to have declared it. */
export function nearestWaypoint(lat: number, lon: number, withinNm = 600): Waypoint | null {
  let best: Waypoint | null = null;
  let bestNm = Infinity;
  for (const a of WAYPOINTS) {
    // Equirectangular is ample at this scale — we are labelling a neighbourhood,
    // not navigating to it.
    const dLat = a.lat - lat;
    const dLon = (a.lon - lon) * Math.cos((((a.lat + lat) / 2) * Math.PI) / 180);
    const nm = Math.hypot(dLat, dLon) * 60;
    if (nm < bestNm) {
      bestNm = nm;
      best = a;
    }
  }
  return bestNm <= withinNm ? best : null;
}
